import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { API_CONFIG } from '../config/api.config';
import api, { setAuthToken } from '../services/api';
import { toast } from '../hooks/use-toast';

// Tipos de dados
interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterData {
  nome: string;
  email: string;
  phone: string;
  password: string;
  password_confirmation: string;
}

interface User {
  id: string;
  nome: string;
  fullName: string;
  email: string;
  phone: string;
  image: string;
  online: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean; // Indica o carregamento da verificação de sessão inicial
  isAuthenticated: boolean;  // Faz a verificação de sessão
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<void>;
  resendConfirmationEmail: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    useEffect(() => {
        const loadUserFronStorage = async () => {
            setError(null);
            setLoading(true);
            try {
                const token = localStorage.getItem(API_CONFIG.TOKEN_KEY);
                if(token) {
                    setAuthToken(token);
                    try{
                        const response = await api.get('/users/me');
                        const userData = response.data;
                        setUser(userData);                
                    } catch (error) {
                        setError((error as Error).message);
                        handleLogout();
                    }
                }
            } catch(error) {
                setError((error as Error).message);
            } finally {
                setLoading(false);
            }
        };

        loadUserFronStorage();
    }, []);


    // Login 
    const login = async (credentials: LoginCredentials) => {
        setLoading(true);
        try {
            const response = await api.post('/auth', credentials);

            if(response.data?.error) {
                toast({
                    title: 'Erro no login',
                    description: `${response?.data?.message}`,
                    variant: 'destructive',
                });
            }
            const token = response.data.token;

            if(response.data && token) {
                localStorage.setItem(API_CONFIG.TOKEN_KEY, token);
                setAuthToken(token);

                // Busca os dados do usuário
                const userResponse = await api.get('/users/me');
                const userData = userResponse.data;
                setUser(userData);
                
                toast({
                    title: "Login Realizado!",
                    description: `Bem-vindo de volta, ${userData.nome}!`,
                    variant: 'success',
                });
            }
        } catch(err: any) {
            setAuthToken(null);
            let errorMessage = "Credenciais inválidas ou erro no servidor.";
            if (typeof err.response?.data === 'string') {
                errorMessage = err.response.data;
            } else {
                errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || errorMessage;
            }
            toast({
                title: 'Erro no login',
                description: errorMessage,
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    /***/
    const register = async (data: RegisterData) => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.post('/auth/signup', data);
            let successMessage = response.data?.message || "Cadastro realizado com sucesso!";
            if (typeof response?.data === 'string') {
                successMessage = response.data || successMessage;
            } else {
                successMessage = response?.data.message || response?.data.success || successMessage;
            }

            toast({
                title: 'Sucesso no Cadastro!',
                description: successMessage,
                variant: 'success',
            });
            

        } catch (erro: any) {
            let errorMessage = "Ocorreu um erro desconhecido.";
            if (typeof erro?.response?.data === 'string') {
                errorMessage = erro.response.data;
            } else {
                errorMessage = erro.response?.data?.error || erro.response?.data?.message || erro.message || errorMessage;
            }
            toast({
                title: 'Erro ao cadastrar',
                description: errorMessage,
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    /***/
    const forgotPassword = async (email: string) => {
        setLoading(true);
        setError(null);
        try {
            const ressetPwd = await api.post(`/auth/resset`, {email: email});

            const msg = ressetPwd.data?.message || "Enviamos o link para sua caixa de email";

            toast({
                title: "",
                description: `${msg}`,
                variant: 'success'
            });
        } catch (erro: any)  {
            let errorReset = "Erro na solicitação";
            if(typeof erro.response?.data === 'string') {
                errorReset = erro.response?.data;
            } else {
                errorReset = erro.response?.data?.error || erro.response?.data?.message || erro.message || errorReset;
            }
            toast({
                title: "",
                description: `${errorReset}`,
                variant: 'destructive'
            });
        } finally {
            setLoading(false);
        }
    };

    /***/
    const resendConfirmationEmail = async () => {
        setLoading(true);
        try {
            const response = await api.post('/auth/resend-confirmation');
            toast({
                title: 'Sucesso!',
                description: response.data.message || 'E-mail de confirmação reenviado.',
                variant: 'success',
            });
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || "Ocorreu um erro ao reenviar o e-mail.";
            toast({
                title: 'Erro',
                description: errorMessage,
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    /**
    * Limpa dados locais do usuário
    */
    const handleLogout = () => {
        localStorage.removeItem(API_CONFIG.TOKEN_KEY);
        setUser(null);
    };

    /***/
    const logout = () => {
        setLoading(false);
        setUser(null);
        setAuthToken(null);
        localStorage.removeItem(API_CONFIG.TOKEN_KEY);
    };

    return (
        <AuthContext.Provider
          value={{
            user,
            loading,
            isAuthenticated: !!user,
            login,
            register,
            logout,
            forgotPassword,
            resendConfirmationEmail,
          }}
        >
          {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
